import { useState, useMemo, useRef } from "react";
import { Bar, getElementAtEvent } from "react-chartjs-2";
import * as utils from "common/utils";
import { Panel, PanelBody } from "components/Panel";
import Button from "components/Button/Button";

export default function ChartByBlocksPLoss({ blocks, farmId, selectedBlock, onBlockSelected }) {
    const chartTopRef = useRef();
    const [showPerHa, setShowPerHa] = useState(true);

    const hasTooManyDataPoints = blocks.length > 0 && blocks.length > utils.MAX_DATA_POINTS;
    if (hasTooManyDataPoints) blocks = [];

    const sortedBlocks = blocks.map((b) => b).sort((a, b) => a.pLossPerHa - b.pLossPerHa);

    const title = `Phosphorus loss ${showPerHa ? "(kg/ha)" : "(kg)"}`;
    const chartData = getChartData(sortedBlocks, farmId, showPerHa, selectedBlock);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const chartOptions = useMemo(() => getChartOptions(sortedBlocks), [sortedBlocks.length]);

    const chartSizeStyle = { position: "relative", height: "500px" };
    if (sortedBlocks.length > 300) chartSizeStyle.width = `${sortedBlocks.length * 5}px`;

    const chartClicked = (event) => {
        const chartElement = getElementAtEvent(chartTopRef.current, event);
        if (!chartElement || !Array.isArray(chartElement) || chartElement.length !== 1 || isNaN(chartElement[0].index)) return;
        if (!onBlockSelected) return;
        if (sortedBlocks.length > chartElement[0].index) {
            const block = sortedBlocks[chartElement[0].index];
            onBlockSelected(block);
        }
    };

    return (
        <Panel title={title} ploss className="u-mt-md">
            <PanelBody>
                <div className="HeroPanel HeroPanel--ploss u-mt-md u-print-border" ref={chartTopRef}>
                    <div className="u-flex u-flexJustifyBetween">
                        <div className="HeroPanel-heading">{title}</div>
                        {!hasTooManyDataPoints && (
                            <div>
                                <div className="Field u-mt-xs">
                                    <div className="Radio_group Radio_group--inline">
                                        <label className="Radio">
                                            <input checked={showPerHa} onChange={() => setShowPerHa(true)} className="Radio-input" type="radio" />
                                            <span className="Radio-label">Show kg per ha</span>
                                        </label>
                                        <label className="Radio u-mr-md">
                                            <input checked={!showPerHa} onChange={() => setShowPerHa(false)} className="Radio-input" type="radio" />
                                            <span className="Radio-label">Show total kg</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {!hasTooManyDataPoints && (
                        <div style={{ overflowX: "auto", overflowY: "hidden" }}>
                            <div style={chartSizeStyle}>
                                <Bar data={chartData} options={chartOptions} onClick={chartClicked} ref={chartTopRef} />
                            </div>
                            <div className="u-flex u-flexJustifyEnd">
                                <Button className="IconLink--search Button Button--secondary u-mt-md" id="add-application-btn" onClick={() => chartTopRef.current.resetZoom()}>
                                    <span>Reset zoom</span>
                                </Button>
                            </div>
                        </div>
                    )}
                    {hasTooManyDataPoints && (
                        <div className="Tile-body-message u-mt-lg u-mb-lg">
                            <i className="icon icon--md icon-alert u-textInfo" />
                            <h3 className="u-mt-md">Benchmarking not supported for comparisons of more than {utils.MAX_DATA_POINTS.toLocaleString()} blocks</h3>
                        </div>
                    )}
                </div>
            </PanelBody>
        </Panel>
    );
};

const getChartData = (blocks, farmId, showPerHa, selectedBlock) => {
    return {
        datasets: [
            {
                data: blocks.map((b) => utils.round((showPerHa ? b.pLossPerHa : b.pLossTotal) || 0, 1)),
                backgroundColor: blocks.map((b) => {
                    if (selectedBlock && selectedBlock.analysisId === b.analysisId && selectedBlock.id === b.id) {
                        return "#ad343e";
                    }
                    if (farmId) {
                        return b.farmId === farmId ? "#e39153" : "rgba(227, 145, 83, 0.1)";
                    }
                    return "#e39153";
                }),
                borderColor: blocks.map((b) => {
                    if (selectedBlock && selectedBlock.analysisId === b.analysisId && selectedBlock.id === b.id) {
                        return "#ad343e";
                    }
                    return "#e39153";
                }),
                borderWidth: 1,
            },
        ],
        labels: blocks.map((b) => (b.farmId === farmId || b.isMyFarm ? `${b.farmName}, ${b.name}` : "")),
    };
};

const getChartOptions = (blocks) => {
    return {
        maintainAspectRatio: false,
        scales: {
            y: { ticks: { beginAtZero: true }, display: true },
            x: { display: false },
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label: (tooltipItem) => {
                        if (blocks.length <= tooltipItem.dataIndex) return tooltipItem.label;
                        const block = blocks[tooltipItem.dataIndex];
                        return `${utils.round(block.pLossTotal || 0, 1).toLocaleString()} kg | ${utils.round(block.pLossPerHa || 0, 1).toLocaleString()} kg/ha`;
                    },
                },
            },
            legend: {
                display: false,
            },
            zoom: {
                pan: {
                    enabled: true,
                    mode: "x",
                    modifierKey: "ctrl",
                },
                zoom: {
                    drag: {
                        enabled: true,
                    },
                    mode: "x",
                },
            },
        },
    };
};
